/**
 * Always-on safety pass.
 *
 * Pasted HTML can come from anywhere, and we put it back on the page (the
 * editor and the visual preview both render it). Setting `innerHTML` does not
 * run `<script>` blocks, but it *does* run things like `<img onerror="…">`, and
 * a `javascript:` link fires on click. None of that is ever wanted here, so it
 * is removed unconditionally — no checkbox, no way to turn it off.
 *
 * This is deliberately separate from the user-facing cleaning options in
 * `clean.ts`: those are about tidiness, this is about not running someone
 * else's code.
 */

/** Tags that are dropped whole (element + contents) no matter what. */
const NEVER_ALLOWED = new Set([
  'script', 'noscript', 'style', 'link', 'meta', 'base', 'title', 'template',
]);

/** Attributes carrying a URL that needs checking for `javascript:` etc. */
const URL_ATTRS = new Set(['href', 'src', 'srcset', 'action', 'formaction', 'poster', 'data', 'xlink:href']);

const UNSAFE_SCHEME = /^\s*(javascript|vbscript|data)\s*:/i;

/** `data:` images are harmless and common in pasted content — allow those. */
const SAFE_DATA_IMAGE = /^\s*data:image\/(png|jpe?g|gif|webp|avif|bmp|x-icon);/i;

/**
 * Strip script-y content from a live DOM subtree, in place.
 */
export function sanitizeTree(root: ParentNode): void {
  // Remove forbidden elements.
  for (const el of Array.from(root.querySelectorAll('*'))) {
    if (NEVER_ALLOWED.has(el.tagName.toLowerCase())) {
      el.remove();
      continue;
    }

    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();

      // Inline event handlers: onclick, onerror, onload, …
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        continue;
      }

      if (URL_ATTRS.has(name) && UNSAFE_SCHEME.test(attr.value)) {
        const isSafeImage = name === 'src' && SAFE_DATA_IMAGE.test(attr.value);
        if (!isSafeImage) el.removeAttribute(attr.name);
      }
    }
  }
}

/**
 * Parse an HTML string into a detached `<body>` element with the safety pass
 * already applied. Everything else in this module works on that body.
 */
export function parseToBody(html: string): HTMLElement {
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html');
  sanitizeTree(doc.body);
  return doc.body;
}

/**
 * Convenience wrapper for anywhere we need to render untrusted HTML: returns a
 * safe HTML string.
 */
export function sanitizeHtml(html: string): string {
  return parseToBody(html).innerHTML;
}
